import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionHeading from '@/components/base/SectionHeading';
import WorkCard from '@/components/base/WorkCard';
import { topics } from '@/mocks/movies';
import { getTopicById, getWorksByTopic } from '@/utils/content';

export default function TopicPage() {
  const { id = '' } = useParams();
  const topic = getTopicById(id);
  const list = topic ? getWorksByTopic(topic) : [];
  const otherTopics = topics.filter((item) => item.id !== id).slice(0, 3);

  useEffect(() => {
    if (topic) {
      document.title = `${topic.title} - 影视专题合集 - 长日光阴`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', topic.description);
    }
  }, [topic]);

  if (!topic) {
    return (
      <div className="mx-auto w-full px-4 py-24 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="专题不存在"
          description="没有找到对应的影视专题，换个入口继续探索吧。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
      </div>
    );
  }

  return (
    <div className="pb-16">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img src={topic.cover} alt={`${topic.title} 封面`} className="h-56 w-full object-top opacity-50 md:h-72" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/30" />
        <div className="absolute inset-0 mx-auto flex w-full flex-col justify-center px-4 md:px-6">
          <span className="text-xs uppercase tracking-[0.2em] text-accent-400">{topic.subtitle}</span>
          <h1 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-4xl">{topic.title}</h1>
          <p className="mt-3 max-w-2xl text-xs leading-relaxed text-background-200 md:text-sm">{topic.description}</p>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视专题', to: '/' }, { label: topic.title }]} />
      </div>

      <div className="mx-auto w-full px-4 md:px-6">
        <SectionHeading title={`专题收录（${list.length} 部）`} description="以下为本专题精选作品，点击卡片查看详细剧情。" />
        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {list.map((work) => (
              <WorkCard key={work.id} work={work} showSynopsis />
            ))}
          </div>
        ) : (
          <EmptyState title="暂无内容，敬请期待" description="该专题的作品正在整理中。" />
        )}
      </div>

      <div className="mx-auto w-full px-4 pt-14 md:px-6">
        <SectionHeading eyebrow="MORE" title="更多专题" linkTo="/" linkLabel="返回首页" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {otherTopics.map((item) => (
            <Link
              key={item.id}
              to={`/topic/${item.id}`}
              className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-accent-400"
            >
              <div className="min-w-0">
                <h3 className="truncate font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-1 text-xs text-secondary-500">{item.workIds.length} 部作品</p>
              </div>
              <i className="ri-arrow-right-line text-lg text-secondary-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}